/**
 * Round 11 — Synergy Round B, presentation-half pure logic
 * (showcase-conversation-synergy.md §6 #5/#6, client-scenarios.md S1 beat 8, S2 beats 4–5).
 *
 * Two service-not-failure signals the server pushes over the existing RTVI `ui.*` channel:
 *   - `ui.gap_noted {topic}`  → a quiet "noted, it'll be in your follow-up ✓" overlay chip on the
 *     showcase. Gigi escalated a question she can't answer (MOQ, European references…); the kiosk
 *     makes the escalation read as a *feature* (the team will handle it), never an error.
 *   - `ui.handoff {status, staffName?}` → the S8 floor-handoff card. A visitor asked to be walked to
 *     a piece on the stand; the server side (`handoffs` table + /staff view) is already live, this
 *     closes the loop on the showcase: "Finding a colleague…" → "{staffName} is on the way ✓".
 *
 * This module is pure (no React / DOM / WS) so the guards + normalisation are unit-testable,
 * mirroring lib/scene.ts. All wire parsing is defensive: the server half (concierge-gg R28) is
 * built in parallel, so we validate shapes at the boundary and drop anything malformed.
 */

// ─── Knowledge-gap "noted" (#6) ──────────────────────────────────────────────

export type GapNote = {
  /** What Gigi escalated (e.g. "minimum order quantity", "European references"). May be empty. */
  topic: string;
};

/**
 * Normalise an incoming `ui.gap_noted` payload into a GapNote, or null if unusable.
 * `topic` is optional on the wire — a bare escalation with no topic still shows the card
 * (the reassurance is the point; the topic is a nice-to-have detail line).
 */
export function parseGapNote(topic: unknown): GapNote | null {
  if (topic === undefined || topic === null) return { topic: '' };
  if (typeof topic !== 'string') return null;
  return { topic: topic.trim() };
}

/** Auto-dismiss window for the gap-noted chip (ms). Long enough to read, short enough to not linger. */
export const GAP_NOTE_DISMISS_MS = 6_000;

// ─── Floor handoff (S8, #5) ──────────────────────────────────────────────────

export const HANDOFF_STATUSES = ['requested', 'assigned', 'arriving'] as const;
export type HandoffStatus = (typeof HANDOFF_STATUSES)[number];

export type Handoff = {
  status: HandoffStatus;
  /** The assigned staff member's name — present once status is assigned/arriving. */
  staffName?: string;
};

export function isHandoffStatus(v: unknown): v is HandoffStatus {
  return typeof v === 'string' && (HANDOFF_STATUSES as readonly string[]).includes(v);
}

/**
 * Normalise an incoming `ui.handoff` payload into a Handoff, or null if the status is unusable.
 * A missing/blank staffName is fine for the 'requested' state ("Finding a colleague…"); for
 * assigned/arriving we keep whatever name arrived (the copy falls back gracefully if it's blank).
 */
export function parseHandoff(status: unknown, staffName: unknown): Handoff | null {
  if (!isHandoffStatus(status)) return null;
  const name =
    typeof staffName === 'string' && staffName.trim() !== '' ? staffName.trim() : undefined;
  return name ? { status, staffName: name } : { status };
}

/** True while the colleague is still being found (spinner copy); false once someone is assigned. */
export function isHandoffPending(h: Handoff): boolean {
  return h.status === 'requested';
}

// ─── On-stand badge (#3) ──────────────────────────────────────────────────────

/**
 * Read the booth-awareness flag from a product payload, tolerant of both the wire field the
 * catalogue payload already ships (`on_stand`, snake_case — shared-types ProductCardPayload) and
 * the `onStand` camelCase the R11 contract/handoff names. concierge-gg R28 may emit either; we
 * accept both so presentation never depends on which spelling lands first.
 */
export function isOnStand(
  item: { on_stand?: boolean; onStand?: boolean } | null | undefined
): boolean {
  if (!item) return false;
  return item.on_stand === true || item.onStand === true;
}
